import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle } from '../components/icons';
import { supabase } from '../lib/supabase';
import { autoCreateLead } from '../lib/crm';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface LeadPageData {
  id: string;
  coach_id: string;
  headline: string;
  description: string;
  cta_text: string;
  image_url: string | null;
}

export function LeadMagnetPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<LeadPageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });

  useEffect(() => {
    if (!slug) { setNotFound(true); setIsLoading(false); return; }

    const load = async () => {
      const { data, error } = await supabase
        .from('coach_lead_pages')
        .select('id, coach_id, headline, description, cta_text, image_url')
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();

      if (error || !data) {
        setNotFound(true);
      } else {
        setPage(data);
      }
      setIsLoading(false);
    };

    load();
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!page) return;
    setIsSubmitting(true);
    setError(null);

    try {
      await autoCreateLead(page.coach_id, {
        full_name: form.name,
        email: form.email,
        source: 'ai_coach_trial',
      });

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Page Not Found</h1>
          <p className="text-gray-500">This coaching page doesn't exist or is no longer active.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-600 to-teal-800">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-xl"
        >
          <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-sage-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">You're In!</h2>
          <p className="text-gray-600">
            Thank you for signing up! You'll receive access to the AI Coach experience shortly.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          {page?.image_url && (
            <img
              src={page.image_url}
              alt=""
              className="w-24 h-24 rounded-full mx-auto mb-6 object-cover border-4 border-white/20 shadow-lg"
            />
          )}
          <h1 className="text-3xl font-bold text-white mb-4">{page?.headline}</h1>
          <p className="text-teal-100 leading-relaxed">{page?.description}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-xl">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : page?.cta_text || 'Get Started'}
            </button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-4">
            We respect your privacy. No spam, unsubscribe anytime.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
